import { FC, useState, useCallback, useEffect, useRef } from 'react';
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogContent,
  DialogTitle,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import SecurityIcon from '@mui/icons-material/Security';
import elevatedAuthService, {
  type ElevatedAuthDispatchCooldownError,
} from 'src/services/elevatedAuth.service';
import OtpCodeInput from 'src/components/shared/OtpCodeInput';
import OtpResendBlock from 'src/components/shared/OtpResendBlock';
import { useOtpResend } from 'src/hooks/useOtpResend';

type ModalState =
  | 'initializing'
  | 'code_entry'
  | 'verifying'
  | 'locked'
  | 'fallback_picker';
type Channel = 'email' | 'sms' | 'totp' | 'backup';

/** ENG-2745: recovery codes are 10-char alphanumeric (matches backend RECOVERY_CODE_LENGTH). */
const RECOVERY_CODE_LENGTH = 10;
/** Standard OTP length for email / SMS / TOTP channels. */
const OTP_CODE_LENGTH = 6;

/**
 * Return true when the error means the channel has no contact on file.
 * The axios interceptor flattens the error body, so error_code lands at the
 * top level of the thrown object rather than under .response.data.
 */
function isUnverifiedContact(err: unknown): boolean {
  return (err as { error_code?: string })?.error_code === 'unverified_contact';
}

/**
 * Extract dispatch-cooldown data from an error, if present.
 *
 * The axios interceptor in src/utils/axios.tsx flattens error.response.data
 * onto the thrown object directly (spreading it alongside a `status` field),
 * so error_code and cooldown_seconds_remaining live at the top level of the
 * thrown value — NOT under .response.data.
 */
function extractDispatchCooldown(
  err: unknown
): ElevatedAuthDispatchCooldownError | null {
  const e = err as {
    error_code?: string;
    cooldown_seconds_remaining?: number;
  };
  if (
    e?.error_code === 'dispatch_cooldown' &&
    typeof e.cooldown_seconds_remaining === 'number'
  ) {
    return e as unknown as ElevatedAuthDispatchCooldownError;
  }
  return null;
}

/**
 * Return true when the error is an OTP-service hourly rate limit
 * (HTTP 429 without a dispatch_cooldown payload).
 *
 * The axios interceptor flattens error.response.data onto the thrown object
 * and adds a top-level `status` field, so we check `err.status` rather than
 * `err.response.status`.
 */
function isHardRateLimit(err: unknown): boolean {
  const httpStatus = (err as { status?: number })?.status;
  if (httpStatus !== 429) {
    return false;
  }
  return extractDispatchCooldown(err) === null;
}

export interface VerifyCodeModalProps {
  purpose: 'elevated_auth' | 'contact_change' | 'login_email_change';
  onSuccess: (expiresAt: string, method: 'otp' | 'totp') => void;
  onCancel?: () => void;
  onUnverifiedContact?: () => void;
  /**
   * Called when the user explicitly skips verification via the
   * "I'll do it later" button on the fallback picker. Only relevant for
   * elevated_auth contexts (Account Hub, Data Vault) where canDismiss is
   * false but we still want a deliberate exit path.
   */
  onSkip?: () => void;
  totpEnabled?: boolean;
  dismissable?: boolean;
  /**
   * Which channel to try first when auto-dispatching an OTP on mount.
   * Defaults to 'email'. Pass 'sms' for users who have no real email.
   */
  preferredChannel?: 'email' | 'sms';
  /**
   * When true, hides the "Can't access your authenticator app?" and
   * "Try another method" fallback links so the user cannot switch away
   * from the required verification channel.
   *
   * Use this when TOTP is specifically required (e.g. the Account Hub
   * re-verify flow for household management) to prevent a user from
   * verifying via email OTP, which would leave the TOTP gate active.
   */
  hideFallback?: boolean;
  /**
   * Optional contextual copy shown above the channel instructions,
   * explaining why verification is required (e.g. editing another household
   * member's contact information).
   */
  description?: string;
}

/**
 * sessionStorage key that records an already-dispatched OTP so that
 * StrictMode double-mounts don't fire a second request within the same
 * mount lifecycle. Scoped to mountId so a fresh modal instance always
 * attempts a new dispatch.
 * Format: "verify_modal_sent:<mountId>:<purpose>:<channel>"
 */
const sentKey = (mountId: string, purpose: string, channel: Channel): string =>
  `verify_modal_sent:${mountId}:${purpose}:${channel}`;

const VerifyCodeModal: FC<VerifyCodeModalProps> = ({
  purpose,
  onSuccess,
  onCancel,
  onUnverifiedContact,
  onSkip,
  totpEnabled = false,
  dismissable: dismissableProp,
  preferredChannel,
  hideFallback = false,
  description,
}) => {
  // A per-mount ID scopes the sessionStorage dedup key so that a fresh mount
  // (e.g. the modal unmounted and re-mounted in a different context) always
  // attempts a new OTP dispatch rather than picking up a stale sent-marker
  // from a prior modal instance.
  const mountId = useRef(
    `${Date.now()}-${Math.random().toString(36).slice(2)}`
  ).current;
  const [modalState, setModalState] = useState<ModalState>('initializing');
  const [activeChannel, setActiveChannel] = useState<Channel | null>(null);
  const [maskedTarget, setMaskedTarget] = useState<string | null>(null);
  const [code, setCode] = useState('');
  const [error, setError] = useState<string | null>(null);

  // On mount: auto-route to the right channel without showing the picker.
  // Guard against hot-reload / StrictMode double-mounts by persisting sent
  // state in sessionStorage — if a code was already dispatched this session,
  // skip the API call and go straight to code_entry.
  useEffect(() => {
    if (totpEnabled) {
      // User has authenticator — no OTP dispatch needed, ask for TOTP code directly
      setActiveChannel('totp');
      setModalState('code_entry');
      return;
    }

    const alreadySentEmail = sessionStorage.getItem(
      sentKey(mountId, purpose, 'email')
    );
    const alreadySentSms = sessionStorage.getItem(
      sentKey(mountId, purpose, 'sms')
    );

    const firstChannel: 'email' | 'sms' = preferredChannel ?? 'email';
    const fallbackChannel: 'email' | 'sms' =
      firstChannel === 'sms' ? 'email' : 'sms';

    const alreadySentFirst =
      firstChannel === 'email' ? alreadySentEmail : alreadySentSms;
    const alreadySentFallback =
      fallbackChannel === 'email' ? alreadySentEmail : alreadySentSms;

    if (alreadySentFirst && alreadySentFirst !== 'pending') {
      setActiveChannel(firstChannel);
      setMaskedTarget(alreadySentFirst !== 'true' ? alreadySentFirst : null);
      setModalState('code_entry');
      return;
    }

    if (alreadySentFallback && alreadySentFallback !== 'pending') {
      setActiveChannel(fallbackChannel);
      setMaskedTarget(
        alreadySentFallback !== 'true' ? alreadySentFallback : null
      );
      setModalState('code_entry');
      return;
    }

    sessionStorage.setItem(sentKey(mountId, purpose, firstChannel), 'pending');

    const dispatchOtp = async (): Promise<void> => {
      try {
        const resp = await elevatedAuthService.requestCode(firstChannel);
        const masked = resp.masked_target ?? null;
        sessionStorage.setItem(
          sentKey(mountId, purpose, firstChannel),
          masked ?? 'true'
        );
        setActiveChannel(firstChannel);
        setMaskedTarget(masked);
        setModalState('code_entry');
      } catch (firstErr) {
        // Hard rate limit (hourly) — surface it directly; don't silently try fallback.
        if (isHardRateLimit(firstErr)) {
          sessionStorage.removeItem(sentKey(mountId, purpose, firstChannel));
          const msg =
            (firstErr as { error?: string })?.error ??
            'Too many requests. Please try again later.';
          setError(msg);
          setModalState('fallback_picker');
          return;
        }
        // Dispatch cooldown — a code was already sent; go to entry.
        const firstCooldown = extractDispatchCooldown(firstErr);
        if (firstCooldown) {
          const storedMasked = sessionStorage.getItem(
            sentKey(mountId, purpose, firstChannel)
          );
          sessionStorage.setItem(
            sentKey(mountId, purpose, firstChannel),
            storedMasked && storedMasked !== 'pending' ? storedMasked : 'true'
          );
          setActiveChannel(firstChannel);
          setMaskedTarget(
            storedMasked &&
              storedMasked !== 'pending' &&
              storedMasked !== 'true'
              ? storedMasked
              : null
          );
          setModalState('code_entry');
          return;
        }
        // No contact on file for first channel — skip fallback, show picker.
        if (isUnverifiedContact(firstErr)) {
          sessionStorage.removeItem(sentKey(mountId, purpose, firstChannel));
          if (onUnverifiedContact) {
            onUnverifiedContact();
            return;
          }
          setModalState('fallback_picker');
          return;
        }
        // First channel unavailable for another reason — try fallback,
        // but only if the fallback channel is usable.
        if (fallbackChannel === 'email' && preferredChannel === 'sms') {
          sessionStorage.removeItem(sentKey(mountId, purpose, firstChannel));
          setModalState('fallback_picker');
          return;
        }
        sessionStorage.removeItem(sentKey(mountId, purpose, firstChannel));
        sessionStorage.setItem(
          sentKey(mountId, purpose, fallbackChannel),
          'pending'
        );
        try {
          const resp = await elevatedAuthService.requestCode(fallbackChannel);
          const masked = resp.masked_target ?? null;
          sessionStorage.setItem(
            sentKey(mountId, purpose, fallbackChannel),
            masked ?? 'true'
          );
          setActiveChannel(fallbackChannel);
          setMaskedTarget(masked);
          setModalState('code_entry');
        } catch (fallbackErr) {
          sessionStorage.removeItem(sentKey(mountId, purpose, fallbackChannel));
          if (isHardRateLimit(fallbackErr)) {
            const msg =
              (fallbackErr as { error?: string })?.error ??
              'Too many requests. Please try again later.';
            setError(msg);
            setModalState('fallback_picker');
            return;
          }
          const fallbackCooldown = extractDispatchCooldown(fallbackErr);
          if (fallbackCooldown) {
            sessionStorage.setItem(
              sentKey(mountId, purpose, fallbackChannel),
              'true'
            );
            setActiveChannel(fallbackChannel);
            setMaskedTarget(null);
            setModalState('code_entry');
            return;
          }
          // Both channels truly failed — show the fallback picker
          if (isUnverifiedContact(fallbackErr) && onUnverifiedContact) {
            onUnverifiedContact();
            return;
          }
          setError(
            'Could not send a verification code automatically. Please choose a method.'
          );
          setActiveChannel(null);
          setModalState('fallback_picker');
        }
      }
    };

    void dispatchOtp();

    // Cleanup: if the component unmounts while the OTP dispatch is still
    // in-flight (e.g. ElevatedAuthGate unmounts the modal during its
    // reconciliation pass), remove any 'pending' markers so the next mount
    // starts a fresh dispatch instead of waiting forever on a dead async call.
    return () => {
      if (
        sessionStorage.getItem(sentKey(mountId, purpose, 'email')) === 'pending'
      ) {
        sessionStorage.removeItem(sentKey(mountId, purpose, 'email'));
      }
      if (
        sessionStorage.getItem(sentKey(mountId, purpose, 'sms')) === 'pending'
      ) {
        sessionStorage.removeItem(sentKey(mountId, purpose, 'sms'));
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps -- intentional: runs once on mount only
  }, []);

  const clearSentState = useCallback((): void => {
    sessionStorage.removeItem(sentKey(mountId, purpose, 'email'));
    sessionStorage.removeItem(sentKey(mountId, purpose, 'sms'));
  }, [mountId, purpose]);

  const sendCode = useCallback(
    async (channel: Channel): Promise<void> => {
      if (channel === 'backup') {
        setActiveChannel('backup');
        setMaskedTarget(null);
        setModalState('code_entry');
        return;
      }
      // Don't dispatch email if there's no valid email on file.
      if (channel === 'email' && preferredChannel === 'sms') {
        return;
      }
      setError(null);
      try {
        const resp = await elevatedAuthService.requestCode(
          channel as 'email' | 'sms' | 'totp'
        );
        const masked = resp.masked_target ?? null;
        if (channel === 'email' || channel === 'sms') {
          sessionStorage.setItem(
            sentKey(mountId, purpose, channel),
            masked ?? 'true'
          );
        }
        setActiveChannel(channel);
        setMaskedTarget(masked);
        setModalState('code_entry');
      } catch (err: unknown) {
        const cooldown = extractDispatchCooldown(err);
        if (cooldown) {
          setModalState('code_entry');
          return;
        }
        setError('Could not send verification code.');
      }
    },
    [mountId, preferredChannel, purpose]
  );

  const submitCode = useCallback(async (): Promise<void> => {
    const requiredLength =
      activeChannel === 'backup' ? RECOVERY_CODE_LENGTH : OTP_CODE_LENGTH;
    if (code.length < requiredLength) {
      return;
    }
    setModalState('verifying');
    setError(null);
    try {
      const result =
        activeChannel === 'backup'
          ? await elevatedAuthService.verifyRecoveryCode(code)
          : await elevatedAuthService.verifyCode(code);
      clearSentState();
      onSuccess(result.expires_at ?? '', result.method);
    } catch (err: unknown) {
      const httpStatus = (err as { response?: { status?: number } })?.response
        ?.status;
      if (httpStatus === 429) {
        setModalState('locked');
      } else {
        setError('Invalid or expired code. Please try again.');
        setModalState('code_entry');
        setCode('');
      }
    }
  }, [activeChannel, code, clearSentState, onSuccess]);

  const resendSendFn = useCallback(async (): Promise<unknown> => {
    if (!activeChannel || activeChannel === 'backup') {
      throw new Error('Invalid channel');
    }
    const result = await elevatedAuthService.requestCode(
      activeChannel as 'email' | 'sms'
    );
    setMaskedTarget(result.masked_target ?? null);
    return result;
  }, [activeChannel]);

  const resend = useOtpResend({
    sendFn: resendSendFn,
    defaultCooldownSeconds: 30,
  });

  const handleTryAnotherMethod = useCallback((): void => {
    setCode('');
    setError(null);
    setModalState('fallback_picker');
  }, []);

  const canDismiss =
    dismissableProp !== undefined
      ? dismissableProp
      : purpose !== 'elevated_auth';

  const handleCancel = useCallback((): void => {
    clearSentState();
    onCancel?.();
  }, [clearSentState, onCancel]);

  const handleSkip = useCallback((): void => {
    clearSentState();
    onSkip?.();
  }, [clearSentState, onSkip]);

  const channelLabel = (ch: Channel | null): string => {
    if (ch === 'totp') {
      return 'your authenticator app';
    }
    if (ch === 'sms') {
      return maskedTarget ? `your phone (${maskedTarget})` : 'your phone';
    }
    if (ch === 'backup') {
      return 'your recovery code';
    }
    return maskedTarget ? `your email (${maskedTarget})` : 'your email';
  };

  return (
    <Dialog
      open
      maxWidth="xs"
      fullWidth
      disableEscapeKeyDown={!canDismiss}
      onClose={canDismiss && onCancel ? handleCancel : undefined}
    >
      <DialogTitle>Verify Your Identity</DialogTitle>
      <DialogContent>
        {/* ---- Initializing: auto-dispatching OTP ---- */}
        {modalState === 'initializing' && (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
            <CircularProgress />
          </Box>
        )}

        {/* ---- Code entry (TOTP or OTP) ---- */}
        {(modalState === 'code_entry' || modalState === 'verifying') && (
          <Stack gap={2.5} alignItems="center">
            <SecurityIcon sx={{ fontSize: 40, color: 'primary.main' }} />

            {description && (
              <Typography
                variant="body2"
                color="text.primary"
                textAlign="center"
              >
                {description}
              </Typography>
            )}

            <Typography
              variant="body2"
              color="text.secondary"
              textAlign="center"
            >
              {activeChannel === 'totp'
                ? 'Open your authenticator app and enter the 6-digit code.'
                : activeChannel === 'backup'
                ? 'Enter one of your recovery codes.'
                : `We sent a 6-digit code to ${channelLabel(activeChannel)}.`}
            </Typography>

            {error && (
              <Typography variant="body2" color="error" textAlign="center">
                {error}
              </Typography>
            )}

            {/* ENG-2745: recovery codes are 10-char alphanumeric — use a plain
                text field instead of the 6-digit split-box OTP input. */}
            {activeChannel === 'backup' ? (
              <TextField
                fullWidth
                value={code}
                onChange={(e) =>
                  setCode(
                    e.target.value
                      .toUpperCase()
                      .replace(/[^A-Z0-9]/g, '')
                      .slice(0, RECOVERY_CODE_LENGTH)
                  )
                }
                disabled={modalState === 'verifying'}
                error={Boolean(error)}
                placeholder="XXXXXXXXXX"
                inputProps={{
                  maxLength: RECOVERY_CODE_LENGTH,
                  style: {
                    textAlign: 'center',
                    letterSpacing: '0.2em',
                    fontFamily: 'monospace',
                    fontSize: '1.2rem',
                  },
                  'aria-label': 'Recovery code',
                }}
              />
            ) : (
              <OtpCodeInput
                value={code}
                onChange={setCode}
                disabled={modalState === 'verifying'}
                error={Boolean(error)}
              />
            )}

            <Button
              variant="contained"
              onClick={() => void submitCode()}
              disabled={
                code.length <
                  (activeChannel === 'backup'
                    ? RECOVERY_CODE_LENGTH
                    : OTP_CODE_LENGTH) || modalState === 'verifying'
              }
              fullWidth
              startIcon={
                modalState === 'verifying' ? (
                  <CircularProgress size={16} />
                ) : null
              }
            >
              <span>
                {modalState === 'verifying' ? 'Verifying…' : 'Verify'}
              </span>
            </Button>

            {/* Resend / Try another method — not shown for TOTP or backup codes */}
            {activeChannel !== 'totp' && activeChannel !== 'backup' && (
              <Stack
                direction="row"
                gap={2}
                flexWrap="wrap"
                justifyContent="center"
                alignItems="center"
              >
                <OtpResendBlock resend={resend} />
                <Button size="small" onClick={handleTryAnotherMethod}>
                  Try another method
                </Button>
              </Stack>
            )}

            {/* For TOTP: allow switching to email/SMS if they can't access the app */}
            {activeChannel === 'totp' && !hideFallback && (
              <Button size="small" onClick={handleTryAnotherMethod}>
                Can&apos;t access your authenticator app?
              </Button>
            )}

            {canDismiss && onCancel && (
              <Button onClick={handleCancel} color="inherit" size="small">
                Cancel
              </Button>
            )}

            {onSkip && modalState === 'code_entry' && (
              <Button
                onClick={handleTryAnotherMethod}
                color="inherit"
                size="small"
              >
                Back
              </Button>
            )}
          </Stack>
        )}

        {/* ---- Fallback picker: only shown if auto-dispatch failed or user chose "try another" ---- */}
        {modalState === 'fallback_picker' && (
          <Stack gap={2}>
            <Typography variant="body2" color="text.secondary">
              Choose how you&apos;d like to verify.
            </Typography>
            {error && <Alert severity="error">{error}</Alert>}
            <Stack gap={1}>
              {totpEnabled && (
                <Button
                  variant="outlined"
                  onClick={() => {
                    setCode('');
                    setError(null);
                    setActiveChannel('totp');
                    setModalState('code_entry');
                  }}
                  fullWidth
                >
                  Authenticator App
                </Button>
              )}
              <Button
                variant="outlined"
                onClick={() => void sendCode('email')}
                disabled={preferredChannel === 'sms'}
                fullWidth
              >
                Send code to email
              </Button>
              <Button
                variant="outlined"
                onClick={() => void sendCode('sms')}
                fullWidth
              >
                Send code via SMS
              </Button>
              {totpEnabled && (
                <Button
                  variant="outlined"
                  onClick={() => void sendCode('backup')}
                  fullWidth
                >
                  Use a recovery code
                </Button>
              )}
            </Stack>
            {canDismiss && onCancel && (
              <Button onClick={handleCancel} color="inherit" size="small">
                Cancel
              </Button>
            )}

            {onSkip && (
              <Button onClick={handleSkip} color="inherit" size="small">
                I&apos;ll do it later
              </Button>
            )}
          </Stack>
        )}

        {/* ---- Locked out ---- */}
        {modalState === 'locked' && (
          <Stack gap={2} alignItems="center" sx={{ py: 2 }}>
            <Typography variant="body1" color="error" textAlign="center">
              Too many failed attempts. Please try again in 10 minutes.
            </Typography>
            {canDismiss && onCancel && (
              <Button onClick={handleCancel} variant="outlined">
                Close
              </Button>
            )}
          </Stack>
        )}
      </DialogContent>
    </Dialog>
  );
};

export default VerifyCodeModal;
